import React, { useEffect, useRef } from 'react';
import './Drawer.css';

/**
 * Drawer — a modal side panel (plan 98, REQ-04 / NFR-03 / REQ-16).
 *
 * Desktop (>768px): right-side panel overlaying the page.
 * Mobile (≤768px): full-screen sheet. The responsive switch is CSS-only
 * (a media query on `.sbd-drawer`), never a JS width class — a correct
 * CSS-only Drawer must not depend on JS viewport detection.
 *
 * Behavior: closes via the close button (data-testid="drawer-close"),
 * backdrop click, and Escape; traps focus while open and restores focus to
 * the triggering element on close; locks body scroll while open;
 * role="dialog" + aria-modal + title-labelled. The `data-testid` prop (and
 * any other DOM attributes) pass through to the dialog element.
 */
const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

function Drawer({ open, onClose, title, subtitle, headerExtra = null, className = '', children, ...rest }) {
  const dialogRef = useRef(null);
  // The element that opened the drawer. We restore focus to it on close.
  // `document.activeElement` is the right answer in a real browser, but a
  // click does not move focus to a <button> in every environment, so we also
  // remember the last interaction target as a fallback.
  const lastInteractionRef = useRef(null);

  useEffect(() => {
    const record = (e) => { lastInteractionRef.current = e.target; };
    document.addEventListener('mousedown', record, true);
    document.addEventListener('click', record, true);
    return () => {
      document.removeEventListener('mousedown', record, true);
      document.removeEventListener('click', record, true);
    };
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    const active = document.activeElement;
    const previouslyFocused =
      active && active !== document.body ? active : lastInteractionRef.current;

    // Move focus inside the drawer (first focusable element, else the dialog).
    const dialog = dialogRef.current;
    if (dialog) {
      const first = dialog.querySelector(FOCUSABLE);
      (first || dialog).focus();
    }

    // Lock body scroll while open.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key === 'Tab' && dialog) {
        const focusable = Array.from(dialog.querySelectorAll(FOCUSABLE)).filter(
          (el) => el.offsetParent !== null || el === document.activeElement,
        );
        if (focusable.length === 0) {
          e.preventDefault();
          dialog.focus();
          return;
        }
        const firstEl = focusable[0];
        const lastEl = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === firstEl) {
          e.preventDefault();
          lastEl.focus();
        } else if (!e.shiftKey && document.activeElement === lastEl) {
          e.preventDefault();
          firstEl.focus();
        }
      }
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus();
      }
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="sbd-drawer-overlay" onClick={onClose}>
      <div
        className={`sbd-drawer ${className}`.trim()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        ref={dialogRef}
        onClick={(e) => e.stopPropagation()}
        {...rest}
      >
        <header className="sbd-drawer-header">
          <div className="sbd-drawer-heading">
            {title && <h2 className="sbd-drawer-title">{title}</h2>}
            {subtitle && <p className="sbd-drawer-subtitle">{subtitle}</p>}
          </div>
          {headerExtra}
          <button
            type="button"
            aria-label="Close"
            className="sbd-drawer-close"
            data-testid="drawer-close"
            onClick={onClose}
          >
            <CloseIcon />
          </button>
        </header>
        <div className="sbd-drawer-body">{children}</div>
      </div>
    </div>
  );
}

export default Drawer;
